/**
 * readAloudTargetId — which document a readout should be read FROM.
 *
 * WHY THIS IS NOT `reactionTargetId` (2026-09-21, Miles: "ensure it's reading
 * the correct message, always, no matter where it's coming from").
 *
 * The speaker control borrowed the reactions key, and the two questions are not
 * the same question. A reaction belongs to a TURN — one reaction document per
 * exchange is exactly what you want, so `reactionTargetId` answers with the task
 * anchor and never looks at the row's own id. A readout belongs to a ROW: the
 * words this bubble is showing. Any row of a turn that is not the anchor row — a
 * file card, a receipt, a surface sibling whose own document is
 * `<anchor>__s<n>` (`surfaceSeriesDocId`) — therefore asked the server for the
 * turn's FINAL ANSWER and was read that instead of itself, with nothing on
 * either side noticing.
 *
 * So: the row's own persisted document id, and the turn anchor only when that IS
 * the row's own document. For the reader's own bubble and for a plain answer the
 * answer is identical to the reactions key, which is why this change reaches
 * only the rows that were wrong.
 *
 * Null means "no speaker on this row" — the same three exclusions reactions
 * make (a system row, the seeded greeting, an answer still streaming), kept
 * identical on purpose: what may be reacted to and what may be read aloud are
 * the same set of real, delivered messages.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';
import { deriveTaskAnchorId } from '../../types/taskAnchor';

type MessageLike = Pick<
  ChatMessage,
  'id' | 'isUser' | 'isSystem' | 'sourceTag' | 'status' | 'senderId'
  | 'sharedMessageId' | 'taskAnchorId' | 'serverTaskId' | 'surfaceSeriesAnchor'
>;

export function readAloudTargetId(
  m: MessageLike | null | undefined,
  selfUid: string | null | undefined,
): string | null {
  if (!m || m.isSystem) return null;
  if (m.sourceTag === 'welcome') return null; // the seeded greeting is chrome
  if (m.status === 'streaming') return null; // not delivered yet
  const own = typeof m.id === 'string' ? m.id.trim() : '';

  if (m.isUser !== true) {
    // A SIBLING IS NOT ITS PARENT. `surfaceSeriesAnchor` carries the PARENT's
    // anchor precisely because this row is an extra surface of that turn, so the
    // anchor is the one id this row must NOT be read from.
    const sibling = typeof m.surfaceSeriesAnchor === 'string' && m.surfaceSeriesAnchor.trim();
    if (sibling) return own || null;
    const anchor = (typeof m.taskAnchorId === 'string' && m.taskAnchorId.trim())
      || deriveTaskAnchorId(m.serverTaskId ?? null)
      || '';
    return anchor || own || null;
  }

  const sender = typeof m.senderId === 'string' ? m.senderId.trim() : '';
  if (!sender || sender === (selfUid ?? '').trim()) {
    const shared = typeof m.sharedMessageId === 'string' ? m.sharedMessageId.trim() : '';
    return shared || own || null;
  }
  return own || null;
}
